import type { ComparisonPerspective, ProductCard, ShowComparisonEnriched } from "@ai-guidance/shared";
import { safeImageUrl, safeProductPath } from "./cards";
import { commerceText, type CommerceCopy } from "./text";

/**
 * MODALUL de COMPARAȚIE (`show_comparison`), faza 2 a comparației.
 *
 * De ce e altceva decât cardurile de produs, deși arată produse: cardurile
 * răspund la „ce ai?", comparația la „care e diferența?". Prima e o listă pe
 * care o parcurgi, a doua e un TABEL pe care îl citești pe rânduri - și doar
 * rândurile fac diferența vizibilă. Coloanele sunt produsele, rândurile sunt
 * perspectivele omenești pe care le-a numit modelul („Pentru cine", „Ce
 * primești pentru diferența de preț"), iar verdictul stă jos, singur, fiindcă e
 * singurul lucru pe care vizitatorul chiar trebuie să-l ducă cu el.
 *
 * De ce are suprafață PROPRIE, nu un loc în panoul de recomandate (schimbat pe
 * 11 aug 2026, după ce s-a văzut pe demo real): panoul de produse e o coloană
 * îngustă, iar un tabel înghesuit în ea se citește pe un cuvânt per rând. Un
 * tabel de comparat e singurul lucru de pe ecran în momentul în care apare -
 * deci primește centrul paginii și un fundal atenuat, pe TOATE suprafețele
 * (panou clasic, orb-chat, voce). Panoul de recomandate rămâne strict al lui
 * `show_products`.
 *
 * Tot conținutul intră prin `textContent`, ca la carduri: titlurile vin din
 * catalogul clientului, iar perspectivele direct din gura modelului.
 */

/** Lățimea minimă la care o coloană rămâne citibilă. Sub ea, tabelul derulează. */
const MIN_COLUMN_PX = 132;

/**
 * Sub toast (2147483040), peste panoul plutitor de carduri (2147483030): o
 * confirmare de coș e un mesaj de o secundă și n-are voie să ajungă sub modal.
 */
const MODAL_Z = 2147483035;

export const COMPARE_CSS = `
/* The comparison is a MODAL, not a card inside the recommendations panel: it is the one
   thing on screen the visitor has to read side by side, so it takes the middle of the page
   over a dimmed backdrop, on every surface (classic panel, orb-chat, voice). It lives in
   the widget's shadow root like everything else here, so the shop's CSS cannot reach it,
   and position:fixed inside a shadow root still resolves against the viewport. */
.aig-cmp-overlay {
  position: fixed; inset: 0; z-index: ${MODAL_Z};
  display: flex; align-items: center; justify-content: center; padding: 24px;
  background: rgba(20,14,44,.44);
  -webkit-backdrop-filter: blur(2px); backdrop-filter: blur(2px);
}
.aig-cmp-overlay[hidden] { display: none; }

/* The same frosted glass as the orb card, deliberately: the assistant has one surface,
   and a second visual language here would read as a third-party pop-up on the shop. */
.aig-compare {
  position: relative; display: flex; flex-direction: column; gap: 10px;
  width: 100%; max-width: min(90vw, 860px); max-height: 85vh;
  padding: 16px 18px; border-radius: 22px;
  border: 1px solid rgba(255,255,255,.55);
  background: linear-gradient(180deg, var(--aig-glass-top, #f6f3ff), var(--aig-glass-bottom, #efe9fb));
  -webkit-backdrop-filter: blur(18px) saturate(140%);
  backdrop-filter: blur(18px) saturate(140%);
  box-shadow: 0 18px 60px rgba(10,6,24,.28);
  color: #262040;
  font-family: system-ui, -apple-system, sans-serif;
}
/* The dialog takes focus on open so the keyboard is inside it; that focus is a mechanism,
   not something to draw, and the visible ring stays on the controls that can be tabbed. */
.aig-compare:focus { outline: none; }
.aig-compare-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.aig-compare-title { font-size: 13px; font-weight: 600; letter-spacing: .01em; color: rgba(38,32,64,.62); }
.aig-compare-close {
  appearance: none; -webkit-appearance: none; font-family: inherit;
  flex: none; width: 28px; height: 28px; border: none; border-radius: 50%; background: transparent;
  color: rgba(38,32,64,.55); font-size: 15px; line-height: 1; cursor: pointer; padding: 0;
  transition: color .2s, background .2s;
}
.aig-compare-close:hover { color: #262040; background: rgba(38,32,64,.08); }
.aig-compare-close:focus-visible { outline: 2px solid var(--aig-primary-ink, #7a51d8); outline-offset: 2px; }

/* Scrolling happens INSIDE the dialog, never on the page behind it: the glass keeps its
   height and the shop does not move under the backdrop.
   Two products fit side by side even at 375px; three do not, and squeezing them would
   make every line wrap to one word. So the table scrolls sideways instead of stacking:
   a stacked comparison is two lists again, which is exactly what it exists not to be. */
.aig-compare-scroll { flex: 1; min-height: 0; overflow: auto; overscroll-behavior: contain; }
.aig-compare-grid {
  display: grid; gap: 8px 12px; align-items: start;
  grid-template-columns: repeat(var(--aig-compare-cols, 2), minmax(${MIN_COLUMN_PX}px, 1fr));
}
/* The product headers stay put while the perspectives scroll under them: a row of notes
   that has lost its column headings is unreadable. The background is the top of the card
   gradient, which is where these columns sit anyway, so nothing shows through them. */
.aig-compare-col {
  display: flex; flex-direction: column; gap: 3px;
  position: sticky; top: 0; z-index: 1;
  padding-bottom: 6px; background: var(--aig-glass-top, #f6f3ff);
}
.aig-compare-img { width: 100%; height: 104px; object-fit: contain; border-radius: 10px; background: rgba(38,32,64,.05); }
.aig-compare-name { font-size: 14px; font-weight: 600; line-height: 1.3; }
.aig-compare-variant { font-size: 12px; color: rgba(38,32,64,.55); }
.aig-compare-price { font-size: 14px; font-weight: 600; }
.aig-compare-was { font-size: 12px; color: rgba(38,32,64,.42); text-decoration: line-through; margin-left: 6px; }
.aig-compare-link { font-size: 12px; color: var(--aig-primary-ink, #7a51d8); text-decoration: none; }
.aig-compare-link:hover { text-decoration: underline; }
.aig-compare-link:focus-visible { outline: 2px solid var(--aig-primary-ink, #7a51d8); outline-offset: 2px; border-radius: 6px; }

/* The perspective label spans the whole table: it is a question asked of both products,
   not a property of either. The hairline above it is what turns a grid into rows. */
.aig-compare-row {
  grid-column: 1 / -1; margin-top: 6px; padding-top: 7px;
  border-top: 1px solid rgba(38,32,64,.10);
  font-size: 12.5px; font-weight: 600; letter-spacing: .01em;
  display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap;
}
.aig-compare-win { font-size: 11px; font-weight: 600; color: var(--aig-primary-ink, #7a51d8); background: rgba(139,92,246,.10); border-radius: 99px; padding: 1px 7px; }
.aig-compare-cell { font-size: 13px; line-height: 1.5; color: rgba(38,32,64,.78); }
.aig-compare-cell--empty { color: rgba(38,32,64,.32); }

/* The verdict is the one sentence the visitor leaves with, so it gets the only tinted
   surface in the panel - and it sits at the bottom, after the reading, not before it.
   Outside the scrolling area on purpose: it stays visible however long the table is. */
.aig-compare-verdict {
  flex: none; padding: 10px 12px; border-radius: 12px;
  background: var(--aig-primary-tint, rgba(139,92,246,.10));
  font-size: 13.5px; line-height: 1.5; font-weight: 500;
}
.aig-compare-verdict-label { display: block; font-size: 10px; letter-spacing: .04em; text-transform: uppercase; color: rgba(38,32,64,.45); margin-bottom: 3px; }

/* On a phone the dialog is the screen, minus a hairline of context so it still reads as a
   layer over the shop rather than as a new page. */
@media (max-width: 640px) {
  .aig-cmp-overlay { padding: 10px; }
  .aig-compare { max-width: none; max-height: calc(100vh - 20px); border-radius: 18px; padding: 12px 14px; gap: 8px; }
  .aig-compare-img { height: 84px; }
}
@media (prefers-reduced-motion: no-preference) {
  .aig-compare { animation: aig-cmp-in .2s ease-out; }
  @keyframes aig-cmp-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
}

/* ------------------------ HUD skin ------------------------
   The same table on the dark instrument surface. Scoped to a class the runtime puts on
   the dialog when the orb-chat surface is active, so a shop on the classic panel gets
   exactly the sheet above. Same glass recipe as the orb card (orbchat/card.ts) - one
   surface per skin, not two dialects of dark. */
.aig-compare--hud {
  border: 1px solid rgba(255,255,255,.14); border-radius: 10px;
  background: rgba(12,12,16,.82);
  -webkit-backdrop-filter: blur(16px) saturate(140%);
  backdrop-filter: blur(16px) saturate(140%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.08), 0 10px 30px rgba(0,0,0,.5);
  color: #f5f6f7;
}
/* Viewfinder brackets, top-left and bottom-right only, in the owner's colour. This is
   the one surface in the skin big enough for them to read as a frame rather than as
   noise, so they stay quiet: 10px arms, hairline, half-transparent. */
.aig-compare--hud::before, .aig-compare--hud::after {
  content: ""; position: absolute; width: 10px; height: 10px;
  pointer-events: none; border: 0 solid var(--aig-orb-glow, #7c3aed); opacity: .55;
}
.aig-compare--hud::before {
  top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; border-top-left-radius: 10px;
}
.aig-compare--hud::after {
  bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; border-bottom-right-radius: 10px;
}
.aig-compare--hud .aig-compare-title { color: rgba(245,246,247,.62); }
.aig-compare--hud .aig-compare-close { color: rgba(245,246,247,.55); }
.aig-compare--hud .aig-compare-close:hover { color: #f5f6f7; background: rgba(255,255,255,.10); }
/* The sticky column headers need the panel's own background under them, or the rows
   would scroll through the product names. */
.aig-compare--hud .aig-compare-col { background: rgba(12,12,16,.96); }
.aig-compare--hud .aig-compare-img { background: rgba(255,255,255,.06); }
.aig-compare--hud .aig-compare-name, .aig-compare--hud .aig-compare-price { color: #f5f6f7; }
.aig-compare--hud .aig-compare-variant { color: rgba(245,246,247,.55); }
.aig-compare--hud .aig-compare-was { color: rgba(245,246,247,.42); }
.aig-compare--hud .aig-compare-link { color: var(--aig-orb-glow, #7c3aed); }
/* Hairlines are what turn the grid into rows - on dark they have to be light, not dark. */
.aig-compare--hud .aig-compare-row { border-top-color: rgba(255,255,255,.10); }
.aig-compare--hud .aig-compare-cell { color: rgba(245,246,247,.78); }
.aig-compare--hud .aig-compare-cell--empty { color: rgba(245,246,247,.32); }
.aig-compare--hud .aig-compare-win { color: var(--aig-orb-glow, #7c3aed); background: rgba(255,255,255,.08); }
.aig-compare--hud .aig-compare-verdict-label { color: rgba(245,246,247,.45); }
/* The verdict keeps the only tinted surface, tinted with the owner's colour at low
   alpha. The plain rgba line first is the fallback for engines without color-mix. */
.aig-compare--hud .aig-compare-verdict {
  background: rgba(255,255,255,.07);
  background: color-mix(in srgb, var(--aig-orb-glow, #7c3aed) 10%, transparent);
}
`;

/** Cine ce spune, pe rândul ăsta: variantId → fraza modelului. */
function notesByVariant(perspective: ComparisonPerspective): Map<string, string> {
  const map = new Map<string, string>();
  for (const note of perspective.notes) if (!map.has(note.variantId)) map.set(note.variantId, note.text);
  return map;
}

/** Antetul unei coloane: imaginea, numele, prețul, linkul. Fără buton de coș. */
function renderColumn(card: ProductCard, copy: CommerceCopy): HTMLElement {
  const column = document.createElement("div");
  column.className = "aig-compare-col";
  column.setAttribute("data-aig-variant", card.variantId);

  const image = safeImageUrl(card.imageUrl);
  if (image) {
    const img = document.createElement("img");
    img.className = "aig-compare-img";
    img.src = image;
    img.alt = "";
    img.loading = "lazy";
    column.appendChild(img);
  }

  const name = document.createElement("div");
  name.className = "aig-compare-name";
  name.textContent = card.title;
  column.appendChild(name);

  if (card.variantTitle) {
    const variant = document.createElement("div");
    variant.className = "aig-compare-variant";
    variant.textContent = card.variantTitle;
    column.appendChild(variant);
  }

  const price = document.createElement("div");
  price.className = "aig-compare-price";
  price.textContent = card.price;
  if (card.compareAtPrice) {
    const was = document.createElement("span");
    was.className = "aig-compare-was";
    was.textContent = card.compareAtPrice;
    price.appendChild(was);
  }
  column.appendChild(price);

  const href = safeProductPath(card.productPath);
  if (href) {
    const link = document.createElement("a");
    link.className = "aig-compare-link";
    link.href = href;
    link.textContent = copy.viewProduct;
    column.appendChild(link);
  }
  return column;
}

/**
 * Cutia de dialog în sine (antet + tabel + verdict), fără overlay și fără
 * comportament. Exportată separat de `openComparisonModal` fiindcă structura se
 * testează singură, iar montarea are nevoie de un shadow root.
 */
export function renderComparison(
  data: ShowComparisonEnriched,
  copy: CommerceCopy = commerceText(),
  onClose?: () => void,
  /** Pielea `hud` e pornită pe suprafață? Vezi `sdk.ts` → `skinRequested`. */
  hud = false,
): HTMLElement {
  const root = document.createElement("div");
  root.className = `aig-compare${hud ? " aig-compare--hud" : ""}`;
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  root.setAttribute("aria-label", copy.comparisonLabel);
  // Focusabil programatic, dar în afara ordinii de Tab: focusul intră aici la
  // deschidere, iar de la el încolo Tab-ul ajunge natural la X și la linkuri.
  root.tabIndex = -1;

  const head = document.createElement("div");
  head.className = "aig-compare-head";
  const title = document.createElement("span");
  title.className = "aig-compare-title";
  title.textContent = copy.comparisonLabel;
  head.appendChild(title);
  const close = document.createElement("button");
  close.className = "aig-compare-close";
  close.type = "button";
  close.setAttribute("aria-label", copy.closePanel);
  close.textContent = "✕";
  close.addEventListener("click", () => {
    if (onClose) {
      onClose();
      return;
    }
    root.remove();
  });
  head.appendChild(close);
  root.appendChild(head);

  const scroll = document.createElement("div");
  scroll.className = "aig-compare-scroll";
  const grid = document.createElement("div");
  grid.className = "aig-compare-grid";
  // Numărul de coloane și lățimea minimă a tabelului sunt calculate de noi, nu
  // primite: `setProperty` cu valori proprii nu e o cale de injecție.
  grid.style.setProperty("--aig-compare-cols", String(data.items.length));
  grid.style.minWidth = `${data.items.length * MIN_COLUMN_PX}px`;

  for (const card of data.items) grid.appendChild(renderColumn(card, copy));

  for (const perspective of data.perspectives) {
    const row = document.createElement("div");
    row.className = "aig-compare-row";
    const label = document.createElement("span");
    label.textContent = perspective.label;
    row.appendChild(label);
    if (perspective.verdict) {
      const win = document.createElement("span");
      win.className = "aig-compare-win";
      win.textContent = perspective.verdict;
      row.appendChild(win);
    }
    grid.appendChild(row);

    // Câte o celulă per coloană, în ordinea coloanelor: un rând cu o gaură ar
    // dezalinia tot ce urmează sub el, iar cine n-are nimic de spus despre un
    // produs spune asta printr-o liniuță, nu prin absență.
    const notes = notesByVariant(perspective);
    for (const card of data.items) {
      const cell = document.createElement("div");
      const text = notes.get(card.variantId);
      cell.className = text ? "aig-compare-cell" : "aig-compare-cell aig-compare-cell--empty";
      cell.textContent = text ?? "–";
      grid.appendChild(cell);
    }
  }

  scroll.appendChild(grid);
  root.appendChild(scroll);

  const verdict = document.createElement("div");
  verdict.className = "aig-compare-verdict";
  const verdictLabel = document.createElement("span");
  verdictLabel.className = "aig-compare-verdict-label";
  verdictLabel.textContent = copy.verdictLabel;
  verdict.appendChild(verdictLabel);
  verdict.appendChild(document.createTextNode(data.verdict));
  root.appendChild(verdict);

  return root;
}

/** Modalul deschis: singurul lucru de făcut cu el din afară e să-l închizi. */
export interface ComparisonModal {
  /** Overlay-ul montat (nodul-rădăcină), pentru teste și pentru demontare. */
  readonly root: HTMLElement;
  close(): void;
}

/**
 * Elementul chiar focusat acum, traversând shadow root-urile: `document.activeElement`
 * se oprește la GAZDA shadow-ului, deci fără coborârea asta am „reda" focusul
 * gazdei widgetului, nu câmpului din care a plecat vizitatorul.
 */
function deepActiveElement(): Element | null {
  let node: Element | null = document.activeElement;
  while (node?.shadowRoot?.activeElement) node = node.shadowRoot.activeElement;
  return node;
}

/**
 * Deschide comparația ca modal centrat pe pagină.
 *
 * `mount` primește overlay-ul: în producție e `mountOverlay` al suprafeței
 * (shadow root-ul widgetului sau al orb-chatului), ca CSS-ul magazinului să nu-l
 * atingă și ca el să nu ajungă în captura trimisă modelului.
 *
 * Trei căi de închidere, fiindcă vizitatorii sunt trei feluri de oameni: X-ul,
 * clicul pe fundal și Escape. Niciuna nu atinge conversația - modalul e o
 * suprafață proprie, nu un mesaj în fir.
 */
export function openComparisonModal(
  data: ShowComparisonEnriched,
  copy: CommerceCopy = commerceText(),
  mount: (node: HTMLElement) => void = (node) => document.body.appendChild(node),
  /**
   * Pielea `hud`: aceeași comparație, pe sticla închisă. Parametru cu valoare
   * implicită, nu obligatoriu - toate apelurile de până acum rămân valide și
   * primesc exact modalul de dinainte.
   */
  hud = false,
): ComparisonModal {
  const overlay = document.createElement("div");
  overlay.className = "aig-cmp-overlay";
  overlay.setAttribute("data-ai-guidance", "comparison-modal");

  let closed = false;
  const returnFocusTo = deepActiveElement();

  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener("keydown", onKeyDown, true);
    overlay.remove();
    // Focusul se întoarce de unde a plecat. Dacă între timp nodul a dispărut din
    // document (o navigare a temei, un panou re-randat), nu forțăm nimic: un
    // `focus()` pe un nod orfan nu duce nicăieri.
    if (returnFocusTo instanceof HTMLElement && returnFocusTo.isConnected) returnFocusTo.focus();
  };

  function onKeyDown(e: KeyboardEvent): void {
    // Overlay-ul poate dispărea din document fără să treacă prin `close` (o temă
    // care rescrie zona, o navigare de tip SPA). Un listener rămas în urmă n-are
    // voie să înghită Escape pentru altcineva, deci se demontează singur.
    if (!overlay.isConnected) {
      document.removeEventListener("keydown", onKeyDown, true);
      return;
    }
    if (e.key !== "Escape") return;
    // Capture + oprire imediată: cât timp modalul e deschis, Escape e AL LUI.
    // Fără asta, aceeași apăsare ar strânge și cardul de orb-chat de dedesubt
    // (`onDocumentKeyDown` în orbchat/index.ts), iar vizitatorul ar pierde două
    // lucruri cu o tastă.
    e.stopImmediatePropagation();
    e.preventDefault();
    close();
  }

  overlay.addEventListener("click", (e) => {
    // Doar fundalul gol: un clic pe sticlă (sau o selecție de text care se
    // termină pe fundal) n-are voie să închidă tabelul.
    if (e.target === overlay) close();
  });
  document.addEventListener("keydown", onKeyDown, true);

  const dialog = renderComparison(data, copy, close, hud);
  overlay.appendChild(dialog);
  mount(overlay);
  dialog.focus();

  return { root: overlay, close };
}
